// The marked-up plan's pure geometry: what the stage frames, how it maps to
// paper, and which tiles that implies. No React, no Konva — extracted from
// PlanStage/plan-export so the flip math, paper math, and tile-request
// derivation are unit-testable and exist exactly once. PlanStage renders
// from these; the export preloads from these; they cannot drift apart.

import type { EditorRoom } from "@/editor-core/document"
import { ringBBox } from "@/editor-core/patterns"
import type { EditorSheetImage } from "@/types/takeoff"
import type { Manifest } from "./TileLayer"

export type ViewRect = { x: number; y: number; w: number; h: number }

export type PaperSize = "letter" | "tabloid"
export type PaperOrientation = "portrait" | "landscape"
export type Paper = { size: PaperSize; orientation: PaperOrientation }

/** The @page size values per paper choice. Explicit inch sizes: "tabloid"
 *  isn't a CSS paged-media keyword (ledger is, with fuzzy orientation
 *  semantics across engines) — inches are unambiguous and Chrome maps them
 *  onto the right named paper in the dialog. MUST stay in lockstep with
 *  paperContentBox (asserted by plan-layout.test.ts). */
export const PAPER_CSS: Record<PaperSize, Record<PaperOrientation, string>> = {
  letter: { portrait: "8.5in 11in", landscape: "11in 8.5in" },
  tabloid: { portrait: "11in 17in", landscape: "17in 11in" },
}

/** Printable content box in CSS px (96 dpi) inside the 0.5in @page margins. */
export function paperContentBox(paper: Paper): { width: number; height: number } {
  const [shortIn, longIn] = paper.size === "tabloid" ? [10, 16] : [7.5, 10]
  const w = paper.orientation === "landscape" ? longIn : shortIn
  const h = paper.orientation === "landscape" ? shortIn : longIn
  return { width: Math.round(w * 96), height: Math.round(h * 96) }
}

/** The slice of the page the figure may claim — the title block above and the
 *  legend below live in the rest. A long legend flows to page two; the
 *  drawing never splits. */
export const FIGURE_BUDGET = 0.7

/** The figure's box on paper: the sheet's own aspect fitted inside the
 *  budgeted content area, so the exported PNG carries no letterbox bands —
 *  what prints is plan edge to plan edge. */
export function figureBox(
  paper: Paper,
  surfaceAspect: number
): { width: number; height: number } {
  const content = paperContentBox(paper)
  const budgetH = content.height * FIGURE_BUDGET
  const width = Math.min(content.width, budgetH * surfaceAspect)
  return { width: Math.round(width), height: Math.round(width / surfaceAspect) }
}

/** Export multiplier targeting `dpi` (default 300 — print grade), backed off
 *  just enough to keep the export canvas under Safari's ~16.7M-pixel canvas
 *  area ceiling. Lower dpi = dramatically cheaper PNG encodes; the page
 *  accepts ?dpi= for lighter debug/test runs. */
export function exportRatioFor(box: { width: number; height: number }, dpi = 300): number {
  const target = Math.min(Math.max(dpi, 96), 300) / 96
  const cap = Math.sqrt(16_000_000 / (box.width * box.height))
  return Math.min(target, cap)
}

/** Everything the layout derivations need — the pure subset of PlanStage's
 *  props (PlanStageProps extends this with the render-only fields). */
export type PlanLayoutInput = {
  rooms: EditorRoom[]
  images: EditorSheetImage[]
  manifests: Record<string, Manifest>
  sheetSize: { width: number; height: number } | null
  sheetKind: "pdf_page" | "blank" | "composite"
  mirrored: boolean
  ftPerPx: number | null
  /** Stage size in CSS px — the paper content box at 96 dpi. */
  width: number
  height: number
  /** Final export px per stage px (dpi/96) — lifts tile resolution to match. */
  exportRatio: number
}

/** What the stage frames: the sheet surface for plan-backed sheets, the
 *  rooms' own bounding box (padded) for blank sheets — printing 400 ft of
 *  empty grid around a sketch helps nobody. */
export function surfaceRect(p: PlanLayoutInput): ViewRect | null {
  const roomBoxes = p.rooms.map((r) => ringBBox(r.outline))
  const union = roomBoxes.length
    ? roomBoxes.reduce((a, b) => {
        const x = Math.min(a.x, b.x)
        const y = Math.min(a.y, b.y)
        return {
          x,
          y,
          w: Math.max(a.x + a.w, b.x + b.w) - x,
          h: Math.max(a.y + a.h, b.y + b.h) - y,
        }
      })
    : null
  if (p.sheetKind === "blank") {
    if (!union) {
      return p.sheetSize ? { x: 0, y: 0, w: p.sheetSize.width, h: p.sheetSize.height } : null
    }
    const pad = p.ftPerPx ? 2 / p.ftPerPx : 40
    return { x: union.x - pad, y: union.y - pad, w: union.w + pad * 2, h: union.h + pad * 2 }
  }
  if (p.sheetSize) return { x: 0, y: 0, w: p.sheetSize.width, h: p.sheetSize.height }
  // No server dims (untiled sheet): frame whatever manifests + rooms cover.
  let rect = union
  for (const img of p.images) {
    const m = p.manifests[String(img.id)]
    if (!m) continue
    const b = { x: img.x, y: img.y, w: m.width * img.scale, h: m.height * img.scale }
    rect = rect
      ? {
          x: Math.min(rect.x, b.x),
          y: Math.min(rect.y, b.y),
          w: Math.max(rect.x + rect.w, b.x + b.w) - Math.min(rect.x, b.x),
          h: Math.max(rect.y + rect.h, b.y + b.h) - Math.min(rect.y, b.y),
        }
      : b
  }
  return rect
}

export function planStageLayout(p: PlanLayoutInput) {
  const surface = surfaceRect(p)
  if (!surface || surface.w <= 0 || surface.h <= 0) return null
  const printScale = Math.min(p.width / surface.w, p.height / surface.h)
  const ox = (p.width - surface.w * printScale) / 2 - surface.x * printScale
  const oy = (p.height - surface.h * printScale) / 2 - surface.y * printScale
  return { surface, printScale, ox, oy }
}

/** Per-image tile parameters — ONE derivation consumed by both PlanStage's
 *  render and the export preloader, so the URLs awaited before an export are
 *  exactly the URLs the mounted stage will draw. Keyed by String(image.id). */
export function planTileRequests(
  p: PlanLayoutInput
): Record<string, { m: Manifest; base: string; stageScale: number; view: ViewRect }> {
  const layout = planStageLayout(p)
  const out: Record<string, { m: Manifest; base: string; stageScale: number; view: ViewRect }> = {}
  if (!layout) return out
  const { printScale, ox, oy } = layout
  const view = {
    x: -ox / printScale,
    y: -oy / printScale,
    w: p.width / printScale,
    h: p.height / printScale,
  }
  const flipW = p.mirrored && p.sheetSize ? p.sheetSize.width : null
  const underlayView =
    flipW != null ? { x: flipW - view.x - view.w, y: view.y, w: view.w, h: view.h } : view
  for (const img of p.images) {
    const m = p.manifests[String(img.id)]
    if (!m) continue
    out[String(img.id)] = {
      m,
      base: img.tiles_base_url,
      stageScale: printScale * p.exportRatio * img.scale,
      view: {
        x: (underlayView.x - img.x) / img.scale,
        y: (underlayView.y - img.y) / img.scale,
        w: underlayView.w / img.scale,
        h: underlayView.h / img.scale,
      },
    }
  }
  return out
}
